<template>
  <q-dialog :model-value="true" :persistent="true">
    <q-card style="min-width: 350px" square>
      <q-form
        ref="formRef"
        @submit.prevent="updatePassword"
        autocomplete="off"
        greedy
      >
        <q-card-section class="flex items-center" v-if="!success">
          <q-icon size="sm" name="mdi-lock-reset" class="q-mr-md" />
          <div class="text-h6">Byt lösenord</div>
        </q-card-section>

        <q-card-section class="flex items-center" v-else>
          <q-icon
            size="sm"
            class="q-mr-md"
            color="primary"
            name="mdi-check-circle-outline"
          />
          <div class="text-h6">Lösenordet uppdaterat</div>
        </q-card-section>

        <q-separator />
        <transition
          enter-active-class="animated animate__fadeIn"
          leave-active-class="animated animate__fadeOut"
          mode="out-in"
          :duration="140"
        >
          <q-card-section v-if="!success">
            <div class="row">
              <div class="col-12">
                <q-input
                  autocomplete="off"
                  dense
                  ref="passwordRef"
                  v-model="model.oldPassword"
                  label="Nuvarande lösenord"
                  :type="!showPasswordOld ? 'password' : 'text'"
                  :rules="[requiredRule, minCharactersRule]"
                >
                  <template v-slot:append>
                    <q-icon
                      :name="showPasswordOld ? 'mdi-eye-off' : 'mdi-eye'"
                      class="cursor-pointer"
                      @click="showPasswordOld = !showPasswordOld"
                    />
                  </template>
                </q-input>

                <q-slide-transition>
                  <div class="row" v-if="showError">
                    <div class="col q-mb-sm">
                      <q-banner dense class="text-white bg-red" rounded>
                        <template v-slot:avatar>
                          <q-icon
                            size="xs"
                            name="mdi-alert-circle-outline"
                            color="white"
                          />
                        </template>
                        Fel lösenord!
                      </q-banner>
                    </div>
                  </div>
                </q-slide-transition>
              </div>
              <div class="col-12">
                <q-input
                  autocomplete="off"
                  dense
                  ref="passwordRef"
                  v-model="model.newPassword"
                  label="Nytt lösenord"
                  :type="!showPasswordNew ? 'password' : 'text'"
                  :rules="[requiredRule, minCharactersRule]"
                >
                  <template v-slot:append>
                    <q-icon
                      :name="showPasswordNew ? 'mdi-eye-off' : 'mdi-eye'"
                      class="cursor-pointer"
                      @click="showPasswordNew = !showPasswordNew"
                    />
                  </template>
                </q-input>
              </div>
            </div>
          </q-card-section>

          <q-card-section v-else> Du har nu bytt lösenord! </q-card-section>
        </transition>

        <q-card-actions align="right" v-if="!success">
          <q-btn
            flat
            type="button"
            label="Avbryt"
            @click="changePasswordModal.closeModal()"
          />
          <q-btn
            flat
            label="Bekräfta"
            type="submit"
            color="primary"
            :loading="pendingChangePassword"
          />
        </q-card-actions>

        <q-card-actions align="right" v-else>
          <q-btn
            flat
            type="button"
            label="Ok"
            color="primary"
            @click="changePasswordModal.closeModal()"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import { defineComponent, inject, ref } from 'vue'
import {
  minCharactersRule,
  required as requiredRule,
} from '@/common/formValidationRules'
import { QForm } from 'quasar'
import { useProfile } from '@/composable/useProfile'
import { UseModal } from '@/composable/useModal'

export default defineComponent({
  name: 'AppChangePasswordModal',

  setup() {
    const success = ref(false)
    const showError = ref(false)
    const { updatePassword: firebaseChangePassword, pendingChangePassword } =
      useProfile()
    const formRef = ref<QForm | null>(null)
    const changePasswordModal = inject<UseModal<unknown>>(
      'change-password-modal'
    )
    const model = ref({
      oldPassword: '',
      newPassword: '',
    })
    const showPasswordOld = ref(false)
    const showPasswordNew = ref(false)

    async function updatePassword() {
      showError.value = false
      const isValid = await formRef.value?.validate()
      if (!isValid) return
      const data = await firebaseChangePassword(model.value)
      if (data === 'no user') {
        showError.value = true
      } else if (data === 'ok') {
        success.value = true
      } else {
        changePasswordModal?.closeModal()
      }
    }
    return {
      changePasswordModal,
      model,
      showPasswordOld,
      showPasswordNew,
      minCharactersRule,
      requiredRule,
      updatePassword,
      formRef,
      pendingChangePassword,
      showError,
      success,
    }
  },
})
</script>

<style lang="scss" module>
.docCode {
  border-radius: 4px;
  overflow: visible;
  padding: 0;
}

.docCodeInner {
  word-wrap: normal;
  background-color: #fdfdfd;
  color: #424242;
  font-family: Consolas, Monaco, Andale Mono, Ubuntu Mono, monospace;
  font-size: 12px;
  -webkit-hyphens: none;
  hyphens: none;
  line-height: 1.5;
  margin: 0;
  position: relative;
  -moz-tab-size: 1;
  tab-size: 1;
  text-align: left;
  white-space: pre;
  word-break: normal;
  word-spacing: normal;
  padding: 4px;
  background-color: #f5f5f5;
  display: block;
  height: inherit;
  max-height: inherit;
  overflow: auto;
}
</style>
